<template>
<div class="mt-1 relative rounded-md shadow-sm">
  <div class="relative cursor-text w-full py-2 pl-3 bg-white border border-gray-500 rounded pr-8"
      :class="[{'border-cardstock-green' : isActive}, {'rounded-b-none' : shouldShowOptions}, {visible : isVisible}, {loading : isLoading}, {search : search}]"
      ref="dropdown"
      @blur="didBlurAway"
      @focus="didFocus">
    <span v-for="(selection, index) in value"
      :key="index"
      v-if="showComboSelections"
      class="border text-sm bg-gray-300 text-black rounded py-1 px-1 inline-block mr-1 mb-1">
      <span>
        <slot name="selection-format" :selection="selection"></slot>
      </span>
      <font-awesome-icon icon="times" @click="didUnselectItem(selection)" class="inline cursor-pointer text-blue-600"/>
    </span>
    <input class="outline-none"
          autocomplete="off"
          v-if="search"
          v-model="proposedSearchEntry"
          v-bind:style="{ width: proposedSearchEntryWidth }"
          ref="search"
          @blur="didBlurAway"
          @keydown="didKeydown"
          @keyup.delete="didKeyupDelete"
          @keydown.up="didKeyupUpArrow"
          @keydown.down="didKeyupDownArrow"
          @keydown.enter.prevent=""
          @keyup.enter="didKeyupEnter"
          @focus="didFocus"
          @input="$emit('search-change', $event.target.value)"
          tabindex="0">

    <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-600er">
      <svg class="h-5 w-5 inline cursor-pointer text-gray-400" fill="currentColor" viewBox="0 0 20 20" @click="didUnselectItem(selection)">
        <font-awesome-icon :icon="['far', 'search']"></font-awesome-icon>
      </svg>
    </div>
    <div class="menu left-0 right-0 max-h-32 overflow-x-hidden overflow-y-auto outline-none -mx-px rounded absolute scrollbar border border-cardstock-green-500" :class="[{visible : shouldShowOptions}, {hidden : !shouldShowOptions}]" ref="menu">
      <div class="menuitem select-none" v-for="(item, index) in filteredItems" @click="didSelectItem(item)" :class="[{disabled: isAlreadyInSelections(item)}, {'bg-gray-300': index === selectedIndex}]" ref="item" :key="index">
        <div class="description" v-if="descriptionFunction(item)">
          {{descriptionFunction(item)}}
        </div>
        <div class="text">
          {{textFunction(item)}}
        </div>
      </div>
      <div class="message" v-if="noMoreItemsLeft">
        No items left
      </div>
      <div class="message" v-else-if="noItemsFound">
        No items found
      </div>
    </div>
  </div>
  <div v-if="showListSelections">
    <div class="p-2 mt-2 flex justify-between" v-for="selection in value" :key="selection.id" >
      <slot name="selection-format" :selection="selection"></slot>
      <div class="select-none" @click="didUnselectItem(selection)" @keyup.enter="didUnselectItem(selection)" tabindex="0">
        <svg class="h-5 w-5 cursor-pointer text-blue-300" fill="currentColor" viewBox="0 0 20 20">
          <font-awesome-icon :icon="['far', 'trash-alt']"></font-awesome-icon>
        </svg>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import _filter from 'lodash-es/filter'
import _find from 'lodash-es/find'
import _union from 'lodash-es/union'
import _without from 'lodash-es/without'

export default {
  components: {

  },

  props: {
    descriptionFunction: { type: Function, required: false, default: function (item) { return '' } },
    isLoading: { type: Boolean, required: false, default: false },
    multiple: { type: Boolean, required: false, default: false },
    possibleItems: { type: Array, required: false, default: () => [] },
    search: { type: Boolean, required: false, default: false },
    showComboSelections: { type: Boolean, required: false, default: true },
    showListSelections: {
      type: Boolean,
      required: false,
      default: false
    },
    showSelectedItemsAsDisabled: { type: Boolean, required: false, default: false },
    textFunction: { type: Function, required: false, default: function (item) { return item.name } },
    value: { default: null }
  },

  data: function () {
    return {
      canDeleteSelection: false,
      currentItemIndex: 0,
      isActive: false,
      isClicking: false,
      isVisible: false,
      proposedSearchEntry: '',
      variableSearchEntryWidth: 8,
      selectedIndex: 0
    }
  },

  mounted: function () {
    document.addEventListener('click', this.didClick, true)
    document.addEventListener('mousedown', this.didClickStart, true)
    document.addEventListener('mouseup', this.didClickFinish, true)
  },

  beforeDestroy () {
    document.removeEventListener('click', this.didClick, true)
    document.removeEventListener('mousedown', this.didClickStart, true)
    document.removeEventListener('mouseup', this.didClickFinish, true)
  },

  methods: {
    didBlurAway: function () {
      if (!this.isClicking) {
        this.proposedSearchEntry = ''
        this.isActive = false
        this.isVisible = false
      }
    },

    didKeydown: function (e) {
      // keyDown happens prior to any characters being deleted
      // Prevents deleting final character and prior selection
      this.canDeleteSelection = this.proposedSearchEntry.length === 0 && this.value.length > 0
    },

    didKeyupDelete: function (e) {
      // see didKeydown above
      if (this.canDeleteSelection) {
        this.didUnselectItem(this.value[this.value.length - 1])
      }
      this.selectedIndex = 0
    },

    didKeyupUpArrow: function () {
      if (this.selectedIndex > 0) {
        this.selectedIndex = this.selectedIndex - 1
      }
      this.scrollToActiveItem()
    },

    didKeyupDownArrow: function () {
      var maxIndex = this.filteredItems.length - 1
      if (this.selectedIndex < maxIndex) {
        this.selectedIndex = this.selectedIndex + 1
      }
      this.scrollToActiveItem()
    },

    didKeyupEnter: function () {
      if (this.noItemsFound) {
        this.proposedSearchEntry = ''
      } else {
        var activeItem = this.filteredItems[this.selectedIndex]
        this.didSelectItem(activeItem)
      }
    },

    didClick: function (e) {
      const el = this.$refs.dropdown
      if (el.contains(e.target)) {
        this.isActive = true
        this.isVisible = true
        if (this.search) {
          this.$refs.search.focus()
        }
      } else {
        this.didBlurAway()
      }
    },

    didClickFinish: function () {
      this.isClicking = false
    },

    didClickStart: function (e) {
      const el = this.$refs.dropdown
      if (el.contains(e.target)) {
        this.isClicking = true
      }
    },

    didFocus: function (e) {
      if (this.search) {
        this.$refs.search.focus()
      }
      this.isActive = true
      this.isVisible = true
    },

    didSelectItem: function (item) {
      this.selectedIndex = 0
      this.proposedSearchEntry = ''
      this.scrollToActiveItem()

      if (this.multiple) {
        this.$emit('input', _union(this.value, [item]))
      } else {
        this.$emit('input', item)
      }
    },

    didUnselectItem: function (item) {
      if (this.multiple) {
        this.$emit('input', _without(this.value, item))
      } else {
        this.$emit('input', null)
      }
    },

    didOpenMenu: function () {
      this.isVisible = true
      if (this.search) {
        this.$refs.search.focus()
      }
    },

    didCloseMenu: function () {
      this.isVisible = false
    },

    escapeRegExp: function (str) {
      return str.replace(/[-[\]/{}()*+?.\\^$|]/g, '\\$&')
    },

    isAlreadyInSelections: function (item) {
      if (!this.multiple) {
        return false
      } else {
        return _find(this.value, ['id', item.id])
      }
    },

    scrollToActiveItem: function () {
      var menu = this.$refs.menu
      var item = this.$refs.item[this.selectedIndex]

      var menuHeight = menu.offsetHeight
      var itemHeight = item.offsetHeight
      var offset = itemHeight * (this.selectedIndex)

      menu.scrollTop = offset - 0.5 * (menuHeight - itemHeight)
    }
  },

  computed: {
    displayText: function () {
      if (this.selection === null || this.multiple) {
        return ''
      } else {
        return this.textFunction(this.selection)
      }
    },

    filteredItems: function () {
      // https://regex101.com/r/Z8KOLp/4
      // For a dropdown item with the .name "Lanny Bose" will match on all of the following
      // values for proposedSearchEntry
      //   - 'lanny bose'
      //   - 'bose lanny'
      //   - 'la bo'
      // with case insensitivity.

      if (this.showSelectedItemsAsDisabled) {
        items = this.possibleItems
      } else {
        var items = _filter(this.possibleItems, (i) => !this.isAlreadyInSelections(i))
      }
      var splitEntry = this.proposedSearchEntry
                           .split(' ')
                           .filter(n => n)
                           .map((str) => this.escapeRegExp(str))
      var regEx = new RegExp('(?=(^|.+)' + splitEntry.join(')(?=(^|.+)') + ')', 'i')

      return _filter(items, (item) => {
        return this.textFunction(item).search(regEx) !== -1 || this.descriptionFunction(item).search(regEx) !== -1
      })
    },

    noItemsFound: function () {
      return this.filteredItems.length === 0
    },

    noMoreItemsLeft() {
      return (this.possibleItems.length == this.value.length) && this.noItemsFound
    },

    proposedSearchEntryWidth () {
      if (this.multiple && this.showComboSelections) {
        return this.variableSearchEntryWidth + 'px'
      } else {
        return '100%'
      }
    },

    shouldShowOptions: function() {
      return this.isVisible && (this.proposedSearchEntry.length > 0)
    }
  },

  watch: {
    proposedSearchEntry: function () {
      // Set the proposedSearchEntryWidth with a temporary sizer span
      var sizer = document.createElement('span')
      sizer.innerHTML = this.proposedSearchEntry
      document.body.appendChild(sizer)
      var width = sizer.getBoundingClientRect().width
      document.body.removeChild(sizer)
      this.variableSearchEntryWidth = width + 8

      // Trim excessive whitespace to prevent obnoxious scrolling
      this.proposedSearchEntry = this.proposedSearchEntry.replace(/\s\s+/g, ' ')
      this.selectedIndex = 0
    }
  }
}
</script>


<style scoped>

.menu {
  border-top-width: 0;
  top: 100%;
  padding: 0 0;
  background: white;
  font-size: 1em;
  text-shadow: none;
  text-align: left;
  z-index: 11;
  word-wrap: break-word;
  line-height: 1em;
  color: rgba(0,0,0,.87);
}

.menuitem {
  border-top: 1px solid #fafafa;
  padding: .78571429rem 1.14285714rem!important;
  white-space: normal;
  word-wrap: normal;
  position: relative;
  cursor: pointer;
  display: block;
  border: none;
  text-align: left;
  line-height: 1em;
  color: rgba(0,0,0,.87);
  font-size: 1rem;
}

.menuitem.selected {
  @apply bg-gray-300;
  color: rgba(0,0,0,.95);
}

.message {
  padding: .78571429rem 1.14285714rem;
  color: rgba(0,0,0,.4);
  margin: 0 -1px;
  line-height: 1em;
}

</style>
